/* eslint-disable i18next/no-literal-string */
import {useState, useEffect, useMemo} from 'react'
import {useMutation, useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {useStepFocus} from '@/hooks/use-step-focus'
import {ArrowLeft, ArrowRight, CheckCircle, AlertCircle, Play, Users, HelpCircle, SkipForward, Search} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useExpressionValidation} from '@/hooks/use-expression-validation'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useRestrictionRuleRequestWizardMode} from '@/features/restriction-rule-requests/hooks/useRestrictionRuleRequestWizardMode'
import {WizardStepHeading} from '@/components/shared/WizardStepHeading'
import {EntityLink} from '@/components/shared/EntityLink'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {ExpressionEditor} from '@/components/ui/expression-editor'
import {SCHEMA_TYPES_IDENTITY, SCHEMA_TYPES_PROFILE} from '@/types/expression'
import {Label} from '@/components/ui/label'
import {Badge} from '@/components/ui/badge'
import {Alert, AlertDescription, AlertTitle} from '@/components/ui/alert'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {RestrictionRuleRequestType} from '@/types/restriction-rule-request'
import type {RestrictionRuleRequestCreateRequest, RestrictionRuleRequestUpdateRequest} from '@/types/restriction-rule-request'
import type {RestrictionRuleTestResult, RestrictionRuleTestResultItem, RestrictionRuleProfile} from '@/types/restriction-rule'
import {useAppConfig} from '@/hooks/use-app-config'
import {OwnersPicker, areOwnersValid} from '@/components/shared/OwnersPicker'
import type {InitialOwnerRequest} from '@/types/owner'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {PageLayout} from '@/components/shared/PageLayout'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {StatValue} from '@/components/shared/StatValue'
import {StatBox} from '@/components/shared/StatBox'
import type {WizardStep as WizardStepType} from '@/components/shared/WizardStepper'
import {InlineRow} from '@/components/shared/InlineRow'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {ScrollableBox} from '@/components/shared/ScrollableBox'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {StatusText} from '@/components/shared/StatusText'
import {IconText} from '@/components/shared/IconText'
import {IconInput} from '@/components/shared/IconInput'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {SmallText} from '@/components/shared/SmallText'

const WizardStep = { BASICS: 0, CONDITION: 1, TEST: 2, PROFILES: 3, REVIEW: 4 } as const
type WizardStep = typeof WizardStep[keyof typeof WizardStep]

type ProfileMode = 'list' | 'condition'

export interface RestrictionRuleRequestWizardProps {
    /** Page mode — `'create'` (default) for new requests, `'edit'` for an existing DRAFT request. */
    mode?: 'create' | 'edit'
    /** ID of the DRAFT request being edited — required when {@code mode === 'edit'}. */
    requestId?: string
    /** Form-state seed for edit-mode hydration. Mirrors the AutoAssign wizard shape. */
    initialData?: {
        name: string
        description?: string
        requestType?: RestrictionRuleRequestType
        ruleId?: string
        conditionExpr?: string
        deniedProfileIds?: string[]
        deniedProfileCondition?: string
        removalGraceDays?: number
        owners?: Array<{ownerPartyId: string; ownerPartyName?: string; responsibility: string}>
        version?: number
    }
}

const EXPRESSION_HELP = `
**Expression Syntax:**
- \`identity.type == 'WORKFORCE'\` — Match identity type
- \`identity.lifecycleState == 'ACTIVE'\` — Match lifecycle state
- \`identity.party.parents.exists(p, p.attributes.code == 'ENG')\` — Match by parent org-unit code
- \`party.displayName CONTAINS 'Admin'\` — String contains

**Operators:**
- Comparison: \`==\`, \`!=\`, \`>\`, \`>=\`, \`<\`, \`<=\`
- String: \`CONTAINS\`, \`STARTS_WITH\`, \`ENDS_WITH\`
- Null: \`IS NULL\`, \`IS NOT NULL\`
- List: \`IN ['value1', 'value2']\`
- Logical: \`AND\`, \`OR\`, \`NOT\`

**Examples:**
\`\`\`
identity.type == 'WORKFORCE' AND identity.lifecycleState == 'ACTIVE'

(identity.type == 'EMPLOYEE' OR identity.type == 'CONTRACTOR')
AND identity.party.parents.exists(p, p.attributes.code IN ['ENG', 'IT'])
\`\`\`
`.trim()

export function RestrictionRuleRequestCreateWizard({
    mode = 'create', requestId, initialData,
}: RestrictionRuleRequestWizardProps = {}) {
    const services = useServices()
    const i18n = useI18n()
    const {gracePeriodDays: availableGracePeriods, restrictionRuleResponsibilityTypes} = useAppConfig()
    const navigate = useNavigate()

    const wizardMode = useRestrictionRuleRequestWizardMode()
    const {isUpdateFromUrl, ruleId, requestTypeParam} = wizardMode
    const isEditMode = mode === 'edit'
    // Update-rule mode (URL-driven) and edit-DRAFT mode (prop-driven) both end
    // up "preloaded" from external data — used to gate the loading spinner +
    // the rule-fetch query. Edit mode itself never fetches the rule.
    const isUpdateMode = isUpdateFromUrl

    usePageTitle(isEditMode || isUpdateMode
        ? i18n.restrictionRuleRequests.editPageTitle
        : i18n.restrictionRuleRequests.createPageTitle)

    // Defensive redirect: ACTIVATE/DEACTIVATE should never land in the wizard
    useEffect(() => {
        if (requestTypeParam === RestrictionRuleRequestType.ACTIVATE || requestTypeParam === RestrictionRuleRequestType.DEACTIVATE) {
            if (ruleId) {
                navigate(routes.accessGovernance.restrictionRules.detail(ruleId), {replace: true})
            } else {
                navigate(routes.accessGovernance.restrictionRules.list(), {replace: true})
            }
        }
    }, [requestTypeParam, ruleId, navigate])

    // Fetch rule data for update mode
    const {data: ruleData, isLoading: ruleLoading} = useQuery({
        queryKey: ['restriction-rule', ruleId],
        queryFn: () => services.restrictionRules.detail(ruleId!),
        enabled: isUpdateMode,
    })

    // Fetch rule profiles for update mode
    const {data: ruleProfilesData = [], isLoading: ruleProfilesLoading} = useQuery({
        queryKey: ['restriction-rule-profiles', ruleId],
        queryFn: () => services.restrictionRules.profiles(ruleId!),
        enabled: isUpdateMode,
    })

    // Wizard state
    const [currentStep, setCurrentStep] = useState<WizardStep>(WizardStep.BASICS)
    const [initialized, setInitialized] = useState(false)

    // Step 1 — Basics
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    // eslint-disable-next-line i18next/no-literal-string -- responsibility is a backend type code
    const [owners, setOwners] = useState<InitialOwnerRequest[]>([{ownerPartyId: '', responsibility: 'BUSINESS'}])
    const [ownerLabels, setOwnerLabels] = useState<Record<string, string>>({})
    const [removalGraceDays, setRemovalGraceDays] = useState(7)
    const [nameError, setNameError] = useState('')
    const [ownerError, setOwnerError] = useState('')

    // Step 2 — Condition
    const [conditionExpr, setConditionExpr] = useState('')
    const [showHelp, setShowHelp] = useState(false)
    const expressionValidation = useExpressionValidation(currentStep === WizardStep.CONDITION ? conditionExpr : '')

    // Step 3 — Test
    const [testResults, setTestResults] = useState<RestrictionRuleTestResult | null>(null)
    const [testError, setTestError] = useState<string | null>(null)
    const [testResultSearch, setTestResultSearch] = useState('')
    const testResultsDialog = useDialog()

    // Step 4 — Denied Profiles
    const [profileMode, setProfileMode] = useState<ProfileMode>('list')
    const [selectedProfileIds, setSelectedProfileIds] = useState<Set<string>>(new Set())
    const [deniedProfileCondition, setDeniedProfileCondition] = useState('')
    const [profileSearchQuery, setProfileSearchQuery] = useState('')

    // Abandon confirmation
    const abandonDialog = useDialog()
    const [pendingNavTarget, setPendingNavTarget] = useState<string | null>(null)

    // Dirty: true once user has entered anything meaningful
    const isDirty = useMemo(
        () => !!name || !!description || owners.some(o => !!o.ownerPartyId) || !!conditionExpr || selectedProfileIds.size > 0 || !!deniedProfileCondition,
        [name, description, owners, conditionExpr, selectedProfileIds, deniedProfileCondition]
    )

    // beforeunload guard for tab close / hard refresh
    useEffect(() => {
        if (!isDirty) return
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ''
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [isDirty])

    // Hydrate from props.initialData (edit-DRAFT mode) — runs before any rule
    // fetch resolves, so the form pre-populates immediately on mount when the
    // caller has the data on hand (e.g. RestrictionRuleRequestEdit fetches the
    // request once and passes the snapshot down).
    useEffect(() => {
        if (!initialData || initialized || isUpdateMode) return
        if (initialData.name !== undefined) setName(initialData.name)
        if (initialData.description !== undefined) setDescription(initialData.description)
        if (initialData.owners?.length) {
            setOwners(initialData.owners.map(o => ({ownerPartyId: o.ownerPartyId, responsibility: o.responsibility})))
            const labels: Record<string, string> = {}
            for (const o of initialData.owners) if (o.ownerPartyName) labels[o.ownerPartyId] = o.ownerPartyName
            setOwnerLabels(prev => ({...prev, ...labels}))
        }
        if (initialData.removalGraceDays !== undefined) setRemovalGraceDays(initialData.removalGraceDays)
        if (initialData.conditionExpr !== undefined) setConditionExpr(initialData.conditionExpr)
        if (initialData.deniedProfileCondition) {
            setDeniedProfileCondition(initialData.deniedProfileCondition)
            setProfileMode('condition')
        } else if (initialData.deniedProfileIds && initialData.deniedProfileIds.length > 0) {
            setSelectedProfileIds(new Set(initialData.deniedProfileIds))
        }
        setInitialized(true)
    }, [initialData, initialized, isUpdateMode])

    // Pre-fill step 1–2 + step 4 from rule in URL update mode (mode=update&ruleId=)
    useEffect(() => {
        if (!ruleData || !isUpdateMode || initialized) return
        setName(ruleData.name)
        setDescription(ruleData.description || '')
        if (ruleData.owners.length > 0) {
            setOwners(ruleData.owners.map(o => ({ownerPartyId: o.ownerPartyId, responsibility: o.responsibility})))
            const labels: Record<string, string> = {}
            for (const o of ruleData.owners) labels[o.ownerPartyId] = o.ownerPartyName
            setOwnerLabels(prev => ({...prev, ...labels}))
        }
        setRemovalGraceDays(ruleData.removalGraceDays)
        setConditionExpr(ruleData.conditionExpr)
        if (ruleData.deniedProfileCondition) {
            setDeniedProfileCondition(ruleData.deniedProfileCondition)
            setProfileMode('condition')
        }
        setInitialized(true)
    }, [ruleData, isUpdateMode, initialized])

    // Pre-fill profile IDs (list mode only) once profiles endpoint responds
    useEffect(() => {
        if ((ruleProfilesData as RestrictionRuleProfile[]).length === 0 || !isUpdateMode || initialized) return
        if (!ruleData?.deniedProfileCondition) {
            setSelectedProfileIds(new Set((ruleProfilesData as RestrictionRuleProfile[]).map(p => p.accessProfile.id)))
        }
    }, [ruleProfilesData, isUpdateMode, initialized, ruleData?.deniedProfileCondition])

    // Profile search
    const {data: profileSearchData} = useQuery({
        queryKey: ['access-profiles', 'search', profileSearchQuery],
        queryFn: () => services.accessProfiles.list({search: profileSearchQuery, size: 20, status: 'ACTIVE'}),
    })

    // Fetch selected profiles for display
    const {data: selectedProfileDetails = []} = useQuery({
        queryKey: ['access-profiles', 'by-ids', Array.from(selectedProfileIds)],
        queryFn: async () => {
            if (selectedProfileIds.size === 0) return []
            const promises = Array.from(selectedProfileIds).map(id =>
                services.accessProfiles.detail(id).catch(() => null)
            )
            return (await Promise.all(promises)).filter((p): p is NonNullable<typeof p> => p !== null)
        },
        enabled: selectedProfileIds.size > 0,
    })

    // Test mutation — calls existing restriction-rules/test endpoint
    // Per ARCH §5.5: display totalEvaluated / matched / violations ONLY — never toAdd/toRemove/unchanged
    const testMutation = useMutation({
        mutationFn: () =>
            services.restrictionRules.test({
                conditionExpr: conditionExpr.trim(),
                profileIds: profileMode === 'list' ? Array.from(selectedProfileIds) : [],
                limit: 50,
            }) as Promise<RestrictionRuleTestResult>,
        onSuccess: (data) => {
            setTestResults(data)
            setTestError(null)
        },
        onError: (err: Error) => {
            setTestError(err.message)
            setTestResults(null)
        },
    })

    // Submit mutation — POST in create mode, PUT in edit mode. Both share the
    // same post-success behaviour (toast + navigate-to-detail) so the wizard
    // is the single owner of the create + edit-DRAFT mutation surface.
    const createMutation = useEntityMutation({
        mutationFn: (data: RestrictionRuleRequestCreateRequest) =>
            services.restrictionRuleRequests.create(data),
        invalidate: ['restriction-rule-requests'],
        successMessage: i18n.restrictionRuleRequests.requestCreated,
        onSuccess: (request: {id: string}) => {
            navigate(routes.accessGovernance.restrictionRuleRequests.detail(request.id))
        },
    })
    const updateMutation = useEntityMutation({
        mutationFn: (data: RestrictionRuleRequestUpdateRequest) =>
            services.restrictionRuleRequests.update(requestId ?? '', data),
        invalidate: [['restriction-rule-requests'], ['restriction-rule-request', requestId]],
        successMessage: i18n.restrictionRuleRequests.requestUpdated,
        onSuccess: (request: {id: string}) => {
            navigate(routes.accessGovernance.restrictionRuleRequests.detail(request.id))
        },
    })
    const submitMutation = isEditMode ? updateMutation : createMutation

    // Wizard step definitions
    const steps: WizardStepType[] = useMemo(() => [
        {id: 'basics', title: i18n.restrictionRuleRequests.wizard.stepBasics},
        {id: 'condition', title: i18n.restrictionRuleRequests.wizard.stepCondition},
        {id: 'test', title: i18n.restrictionRuleRequests.wizard.stepTest},
        {id: 'profiles', title: i18n.restrictionRuleRequests.wizard.stepProfiles},
        {id: 'review', title: i18n.restrictionRuleRequests.wizard.stepReview},
    ], [i18n])

    const stepContainerRef = useStepFocus(currentStep, String(steps[currentStep].title))

    const stepDescriptions: string[] = [
        i18n.restrictionRuleRequests.wizard.stepBasicsDesc,
        i18n.restrictionRuleRequests.wizard.stepConditionDesc,
        i18n.restrictionRuleRequests.wizard.stepTestDesc,
        i18n.restrictionRuleRequests.wizard.stepProfilesDesc,
        i18n.restrictionRuleRequests.wizard.stepReviewDesc,
    ]

    // Step validation
    const isStepValid = (step: WizardStep): boolean => {
        switch (step) {
            case WizardStep.BASICS:
                return !!name.trim() && areOwnersValid(owners)
            case WizardStep.CONDITION:
                return !!conditionExpr.trim()
            case WizardStep.TEST:
                return true
            case WizardStep.PROFILES:
                return profileMode === 'condition'
                    ? true
                    : selectedProfileIds.size > 0
            case WizardStep.REVIEW:
                return true
            default:
                return false
        }
    }

    const validateStep1 = () => {
        let valid = true
        if (!name.trim()) { setNameError(i18n.restrictionRuleRequests.nameRequired); valid = false }
        else { setNameError('') }
        if (!areOwnersValid(owners)) { setOwnerError(i18n.restrictionRuleRequests.ownerRequired); valid = false }
        else { setOwnerError('') }
        return valid
    }

    const goNext = () => {
        if (currentStep === WizardStep.BASICS && !validateStep1()) return
        if (currentStep < steps.length - 1 && isStepValid(currentStep)) {
            setCurrentStep(s => (s + 1) as WizardStep)
        }
    }

    const goBack = () => {
        if (currentStep > WizardStep.BASICS) {
            setCurrentStep(s => (s - 1) as WizardStep)
        } else if (isDirty) {
            setPendingNavTarget(null)
            abandonDialog.open()
        } else {
            navigate(-1)
        }
    }

    const confirmAbandon = () => {
        abandonDialog.close()
        if (pendingNavTarget) {
            navigate(pendingNavTarget)
        } else {
            navigate(-1)
        }
    }

    const handleSubmit = async () => {
        const profilePayload = profileMode === 'list'
            ? {deniedProfileIds: Array.from(selectedProfileIds)}
            : {deniedProfileCondition: deniedProfileCondition.trim() || undefined}

        if (isEditMode) {
            const updatePayload: RestrictionRuleRequestUpdateRequest = {
                name: name.trim(),
                description: description.trim() || undefined,
                owners,
                removalGraceDays,
                conditionExpr: conditionExpr.trim() || undefined,
                ...profilePayload,
                version: initialData?.version ?? 0,
            }
            await updateMutation.mutateAsync(updatePayload)
            return
        }

        await createMutation.mutateAsync({
            requestType: isUpdateMode ? RestrictionRuleRequestType.UPDATE : RestrictionRuleRequestType.CREATE,
            ...(isUpdateMode && ruleId ? {ruleId} : {}),
            name: name.trim(),
            description: description.trim() || undefined,
            owners,
            removalGraceDays,
            conditionExpr: conditionExpr.trim(),
            ...profilePayload,
        })
    }

    const handleProfileModeChange = (mode: ProfileMode) => {
        if (mode === 'list') setDeniedProfileCondition('')
        else setSelectedProfileIds(new Set())
        setProfileMode(mode)
    }

    const removeProfile = (id: string) => setSelectedProfileIds(prev => { const n = new Set(prev); n.delete(id); return n })
    const toggleProfile = (id: string) => setSelectedProfileIds(prev => {
        const n = new Set(prev)
        if (n.has(id)) n.delete(id); else n.add(id)
        return n
    })

    // Filter test results by search
    const filteredTestResults = useMemo(() => {
        if (!testResults?.items) return []
        if (!testResultSearch) return testResults.items
        const search = testResultSearch.toLowerCase()
        return testResults.items.filter((item: RestrictionRuleTestResultItem) =>
            item.identity.displayName?.toLowerCase().includes(search) ||
            item.party.displayName?.toLowerCase().includes(search)
        )
    }, [testResults, testResultSearch])

    // ── Step renderers ────────────────────────────────────────────────────────

    const renderBasics = () => (
        <Stack gap="lg">
            <div>
                <Label htmlFor="wizard-name">{i18n.restrictionRuleRequests.wizard.nameLabel} *</Label>
                <Input
                    id="wizard-name"
                    data-testid="input-name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder={i18n.restrictionRuleRequests.namePlaceholder}
                    className="mt-1"
                />
                {nameError && (
                    <MutedText size="sm" className="text-destructive mt-1" data-testid="error-name">
                        {nameError}
                    </MutedText>
                )}
            </div>

            <div>
                <Label htmlFor="wizard-description">{i18n.common.description}</Label>
                <Textarea
                    id="wizard-description"
                    data-testid="input-description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder={i18n.restrictionRuleRequests.descriptionPlaceholder}
                    className="mt-1"
                    rows={3}
                />
            </div>

            <div>
                <Label>{i18n.restrictionRuleRequests.ownerPartyLabel} *</Label>
                <OwnersPicker
                    owners={owners}
                    onOwnersChange={setOwners}
                    responsibilityTypes={restrictionRuleResponsibilityTypes}
                    ownerLabels={ownerLabels}
                    onOwnerLabelsChange={setOwnerLabels}
                />
                {ownerError && (
                    <MutedText size="sm" className="text-destructive mt-1" data-testid="error-owner">
                        {ownerError}
                    </MutedText>
                )}
            </div>

            <div>
                <Label>{i18n.restrictionRuleRequests.removalGraceDaysLabel}</Label>
                <InlineRow gap="sm" className="mt-1" data-testid="grace-days-picker">
                    {(availableGracePeriods?.length > 0 ? availableGracePeriods : [0, 7, 14, 30]).map((days: number) => (
                        <Button
                            key={days}
                            type="button"
                            variant={removalGraceDays === days ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setRemovalGraceDays(days)}
                            data-testid={`btn-grace-days-${days}`}
                        >
                            {days} {i18n.restrictionRuleRequests.days}
                        </Button>
                    ))}
                </InlineRow>
            </div>

            {isUpdateMode && (
                <InfoBanner variant="info">
                    <SmallText>
                        {i18n.restrictionRuleRequests.wizard.updateModeNote}
                    </SmallText>
                </InfoBanner>
            )}
        </Stack>
    )

    const renderCondition = () => (
        <Stack>
            <InlineRow justify="between">
                <Label htmlFor="wizard-condition">{i18n.restrictionRuleRequests.conditionExprLabel}</Label>
                <Collapsible open={showHelp} onOpenChange={setShowHelp}>
                    <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" type="button" data-testid="btn-toggle-help">
                            <Icon icon={HelpCircle} className="mr-1"/>
                            {showHelp
                                ? i18n.restrictionRuleRequests.wizard.hideHelp
                                : i18n.restrictionRuleRequests.wizard.showHelp}
                        </Button>
                    </CollapsibleTrigger>
                </Collapsible>
            </InlineRow>

            <Collapsible open={showHelp} onOpenChange={setShowHelp}>
                <CollapsibleContent>
                    <CodeBlock>{EXPRESSION_HELP}</CodeBlock>
                </CollapsibleContent>
            </Collapsible>

            <ExpressionEditor
                value={conditionExpr}
                onChange={setConditionExpr}
                placeholder={i18n.restrictionRuleRequests.conditionExprPlaceholder as string}
                rows={6}
                schemaTypes={SCHEMA_TYPES_IDENTITY}
                data-testid="input-condition-expr"
            />

            {!conditionExpr.trim() && (
                <StatusText tone="destructive" size="sm" data-testid="condition-empty-warning">
                    <IconText icon={AlertCircle}>
                        {i18n.restrictionRuleRequests.wizard.conditionRequired}
                    </IconText>
                </StatusText>
            )}

            {conditionExpr.trim() && expressionValidation.isValidating && (
                <StatusText tone="info" size="sm" data-testid="expression-validating">
                    {i18n.restrictionRuleRequests.wizard.expressionValidating}
                </StatusText>
            )}

            {conditionExpr.trim() && !expressionValidation.isValidating && expressionValidation.error && (
                <StatusText tone="warning" size="sm" data-testid="expression-network-error">
                    <IconText icon={AlertCircle}>
                        {i18n.restrictionRuleRequests.wizard.expressionNetworkError}
                    </IconText>
                </StatusText>
            )}

            {conditionExpr.trim() && !expressionValidation.isValidating && !expressionValidation.error && expressionValidation.result?.valid && (
                <StatusText tone="success" size="sm" data-testid="expression-valid-indicator">
                    <IconText icon={CheckCircle}>
                        {i18n.restrictionRuleRequests.wizard.expressionValid}
                    </IconText>
                </StatusText>
            )}

            {conditionExpr.trim() && !expressionValidation.isValidating && !expressionValidation.error && expressionValidation.result && !expressionValidation.result.valid && (
                <Stack gap="sm" data-testid="expression-invalid-indicator">
                    <StatusText tone="destructive" size="sm">
                        <IconText icon={AlertCircle}>
                            {i18n.restrictionRuleRequests.wizard.expressionInvalid}
                        </IconText>
                    </StatusText>
                    {expressionValidation.result.errors.map((err, idx) => (
                        <StatusText key={idx} tone="destructive" size="sm" data-testid={`expression-error-${idx}`}>
                            {i18n.restrictionRuleRequests.wizard.expressionParseError({line: err.line, column: err.column, message: err.message})}
                        </StatusText>
                    ))}
                </Stack>
            )}
        </Stack>
    )

    const renderTest = () => (
        <Stack>
            <Stack gap="sm">
                <MutedText block centered>{i18n.restrictionRuleRequests.wizard.testDescription}</MutedText>
                <Button
                    onClick={() => testMutation.mutate()}
                    disabled={testMutation.isPending || !conditionExpr.trim()}
                    size="lg"
                    data-testid="btn-run-test"
                >
                    {testMutation.isPending ? (
                        <><ButtonSpinner/>{i18n.restrictionRules.testing}</>
                    ) : (
                        <><Icon icon={Play} className="mr-2"/>{i18n.restrictionRuleRequests.wizard.runTest}</>
                    )}
                </Button>
            </Stack>

            {testError && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4"/>
                    <AlertTitle>{i18n.restrictionRules.testFailed}</AlertTitle>
                    <AlertDescription>{testError}</AlertDescription>
                </Alert>
            )}

            {testResults && (
                <Stack>
                    <Alert data-testid="test-results-success" className="bg-status-success-subtle border-green-200">
                        <InlineRow justify="between" align="start" className="w-full">
                            <InlineRow gap="sm" className="items-start">
                                <Icon icon={Users} className="text-status-success mt-0.5"/>
                                <div>
                                    <AlertTitle className="text-status-success">
                                        {i18n.restrictionRuleRequests.wizard.testCompleted}
                                    </AlertTitle>
                                    <AlertDescription className="text-status-success">
                                        {i18n.restrictionRuleRequests.wizard.testCompletedDesc({
                                            total: testResults.totalEvaluated,
                                            matched: testResults.matched,
                                            violations: testResults.violations,
                                        })}
                                    </AlertDescription>
                                </div>
                            </InlineRow>
                            {testResults.items && testResults.items.length > 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => { setTestResultSearch(''); testResultsDialog.open() }}
                                    data-testid="btn-view-affected"
                                >
                                    <Icon icon={Users} className="mr-2"/>
                                    {i18n.restrictionRuleRequests.wizard.viewAffected({count: testResults.items.length})}
                                </Button>
                            )}
                        </InlineRow>
                    </Alert>

                    {/* Restriction-rule semantics only — never auto-assign toAdd/toRemove/unchanged */}
                    <DetailGrid columns={3} responsive={false}>
                        <StatBox variant="neutral">
                            <StatValue value={testResults.totalEvaluated}/>
                            <MutedText size="xs">{i18n.restrictionRuleRequests.wizard.statEvaluated}</MutedText>
                        </StatBox>
                        <StatBox variant="success">
                            <StatValue value={testResults.matched} variant="success"/>
                            <MutedText size="xs">{i18n.restrictionRuleRequests.wizard.statMatched}</MutedText>
                        </StatBox>
                        <StatBox variant="danger">
                            <StatValue value={testResults.violations} variant="danger"/>
                            <MutedText size="xs">{i18n.restrictionRuleRequests.wizard.statViolations}</MutedText>
                        </StatBox>
                    </DetailGrid>
                </Stack>
            )}

            <Dialog open={testResultsDialog.isOpen} onOpenChange={testResultsDialog.bind.onOpenChange}>
                <DialogContent className="max-w-3xl h-[600px]">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>{i18n.restrictionRules.viewAffectedIdentities}</DialogTitle>
                    </DialogHeader>
                    <Stack gap="md" align="stretch" className="flex-1 min-h-0">
                        <InlineRow className="flex-shrink-0">
                            <IconInput
                                icon={Search}
                                inputSize="sm"
                                widthClass="w-64"
                                placeholder={i18n.restrictionRuleRequests.wizard.searchIdentities}
                                value={testResultSearch}
                                onChange={e => setTestResultSearch(e.target.value)}
                            />
                        </InlineRow>
                        <ScrollableBox grow flush>
                            <Table>
                                <TableHeader sticky>
                                    <TableRow>
                                        <TableHead data-testid="th-test-result-identity">{i18n.restrictionRules.identity}</TableHead>
                                        <TableHead data-testid="th-test-result-owner">{i18n.restrictionRules.owner}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredTestResults.length === 0 ? (
                                        <TableEmptyState
                                            colSpan={2}
                                            hasFilters={false}
                                            emptyMessage={i18n.restrictionRuleRequests.wizard.noIdentitiesMatch as string}
                                            matchMessage={i18n.restrictionRuleRequests.wizard.noIdentitiesMatch as string}
                                        />
                                    ) : (
                                        filteredTestResults.map((item: RestrictionRuleTestResultItem) => (
                                            <TableRow key={item.identity.id}>
                                                <TableCell className="font-medium">
                                                    <EntityLink value={item.identity} testId={`link-identity-detail-${item.identity.id}`}/>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    <EntityLink value={item.party} testId={`link-party-detail-${item.party.id}`}/>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollableBox>
                    </Stack>
                </DialogContent>
            </Dialog>

            <InfoBanner variant="muted">
                <Stack gap="xs">
                    <Strong>{i18n.restrictionRuleRequests.wizard.expressionBeingTested}</Strong>
                    <CodeBlock>{conditionExpr}</CodeBlock>
                </Stack>
            </InfoBanner>
        </Stack>
    )

    const renderProfiles = () => (
        <Stack>
            <div>
                <Label>{i18n.restrictionRuleRequests.deniedProfilesLabel}</Label>
                <InlineRow gap="sm" className="mt-2" data-testid="profile-mode-toggle">
                    <Button
                        type="button"
                        variant={profileMode === 'list' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleProfileModeChange('list')}
                        data-testid="btn-profile-mode-list"
                    >
                        {i18n.restrictionRuleRequests.profileModeList}
                    </Button>
                    <Button
                        type="button"
                        variant={profileMode === 'condition' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleProfileModeChange('condition')}
                        data-testid="btn-profile-mode-condition"
                    >
                        {i18n.restrictionRuleRequests.profileModeCondition}
                    </Button>
                </InlineRow>
            </div>

            {profileMode === 'list' ? (
                <Stack gap="sm">
                    {selectedProfileDetails.length > 0 && (
                        <InlineRow wrap gap="sm" data-testid="selected-profiles-list">
                            {selectedProfileDetails.map((profile) => (
                                <Badge key={profile.id} variant="secondary">
                                    <InlineRow gap="xs">
                                        {profile.name}
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeProfile(profile.id)}
                                            data-testid={`btn-remove-profile-${profile.id}`}
                                        >
                                            ×
                                        </Button>
                                    </InlineRow>
                                </Badge>
                            ))}
                        </InlineRow>
                    )}

                    <Input
                        placeholder={i18n.restrictionRuleRequests.searchProfilesToAdd}
                        value={profileSearchQuery}
                        onChange={e => setProfileSearchQuery(e.target.value)}
                        data-testid="input-profile-search"
                    />

                    {profileSearchData && profileSearchData.content.length === 0 ? (
                        <MutedText size="sm" className="p-3">{i18n.common.noResults}</MutedText>
                    ) : profileSearchData && (
                        <ScrollableBox maxHeight="sm">
                            <Table>
                                <TableBody>
                                    {profileSearchData.content.map((profile) => (
                                        <TableRow
                                            key={profile.id}
                                            className="cursor-pointer"
                                            onClick={() => toggleProfile(profile.id)}
                                            data-testid={`row-profile-${profile.id}`}
                                        >
                                            <TableCell className="w-10">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedProfileIds.has(profile.id)}
                                                    onChange={() => {}}
                                                    className="h-4 w-4"
                                                    data-testid={`checkbox-profile-${profile.id}`}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Strong>{profile.name}</Strong>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollableBox>
                    )}

                    {selectedProfileIds.size === 0 && (
                        <MutedText size="sm" data-testid="no-profiles-selected">
                            {i18n.restrictionRuleRequests.wizard.noDeniedProfiles}
                        </MutedText>
                    )}
                </Stack>
            ) : (
                <Stack gap="sm">
                    <Label>{i18n.restrictionRuleRequests.profileConditionLabel}</Label>
                    <ExpressionEditor
                        value={deniedProfileCondition}
                        onChange={setDeniedProfileCondition}
                        placeholder={i18n.restrictionRuleRequests.profileConditionPlaceholder as string}
                        rows={3}
                        data-testid="input-profile-condition"
                        schemaTypes={SCHEMA_TYPES_PROFILE}
                    />
                </Stack>
            )}
        </Stack>
    )

    const renderReview = () => (
        <Stack gap="lg">
            <DetailGrid columns={2} responsive={false}>
                <div>
                    <MutedText className="font-medium block">{i18n.common.name}</MutedText>
                    <Strong>{name}</Strong>
                </div>
                <div>
                    <MutedText className="font-medium block">{i18n.restrictionRuleRequests.ownerPartyLabel}</MutedText>
                    <Strong>{owners.length > 0
                        ? owners.map(o => ownerLabels[o.ownerPartyId] ?? o.ownerPartyId).join(', ')
                        : '—'}</Strong>
                </div>
                <div>
                    <MutedText className="font-medium block">{i18n.restrictionRuleRequests.removalGraceDaysLabel}</MutedText>
                    <Strong>{removalGraceDays} {i18n.restrictionRuleRequests.days}</Strong>
                </div>
                <div>
                    <MutedText className="font-medium block">{i18n.restrictionRuleRequests.requestTypeLabel}</MutedText>
                    <Strong>
                        {isUpdateMode ? i18n.restrictionRuleRequests.typeUpdate : i18n.restrictionRuleRequests.typeCreate}
                    </Strong>
                </div>
            </DetailGrid>

            {description && (
                <div>
                    <MutedText className="font-medium block">{i18n.common.description}</MutedText>
                    <MutedText>{description}</MutedText>
                </div>
            )}

            <div>
                <MutedText block weight="medium">{i18n.restrictionRuleRequests.conditionExprLabel}</MutedText>
                <CodeBlock>{conditionExpr}</CodeBlock>
            </div>

            <div>
                <MutedText block weight="medium">{i18n.restrictionRuleRequests.deniedProfilesLabel}</MutedText>
                {profileMode === 'condition' ? (
                    <CodeBlock>{deniedProfileCondition || '—'}</CodeBlock>
                ) : selectedProfileDetails.length > 0 ? (
                    <InlineRow wrap gap="sm">
                        {selectedProfileDetails.map(p => (
                            <Badge key={p.id} variant="outline">{p.name}</Badge>
                        ))}
                    </InlineRow>
                ) : (
                    <MutedText size="sm">—</MutedText>
                )}
            </div>

            {testResults ? (
                <Alert data-testid="review-test-summary" className="bg-status-success-subtle border-green-200">
                    <Icon icon={Users} className="text-status-success"/>
                    <AlertTitle className="text-status-success">
                        {i18n.restrictionRuleRequests.wizard.testResultSummary}
                    </AlertTitle>
                    <AlertDescription className="text-status-success">
                        {i18n.restrictionRuleRequests.wizard.testCompletedDesc({
                            total: testResults.totalEvaluated,
                            matched: testResults.matched,
                            violations: testResults.violations,
                        })}
                    </AlertDescription>
                </Alert>
            ) : (
                <MutedText size="sm" data-testid="not-tested-note">
                    {i18n.restrictionRuleRequests.wizard.notTested}
                </MutedText>
            )}
        </Stack>
    )

    const renderStepContent = () => {
        switch (currentStep) {
            case WizardStep.BASICS:    return renderBasics()
            case WizardStep.CONDITION: return renderCondition()
            case WizardStep.TEST:      return renderTest()
            case WizardStep.PROFILES:  return renderProfiles()
            case WizardStep.REVIEW:    return renderReview()
            default:                   return null
        }
    }

    const isLastStep = currentStep === (steps.length - 1) as WizardStep

    const wizardNavigation = (
        <>
            <Button
                variant="outline"
                onClick={goBack}
                data-testid="btn-previous-step"
            >
                <Icon icon={ArrowLeft} className="mr-2"/>
                {i18n.common.back}
            </Button>

            {currentStep === WizardStep.TEST && (
                <Button
                    variant="ghost"
                    onClick={goNext}
                    data-testid="btn-skip-test"
                >
                    <Icon icon={SkipForward} className="mr-2"/>
                    {i18n.restrictionRuleRequests.wizard.skipTest}
                </Button>
            )}

            {!isLastStep ? (
                <Button
                    onClick={goNext}
                    disabled={!isStepValid(currentStep)}
                    data-testid="btn-next-step"
                >
                    {i18n.restrictionRuleRequests.wizard.next}
                    <Icon icon={ArrowRight} className="ml-2"/>
                </Button>
            ) : (
                <Button
                    onClick={handleSubmit}
                    disabled={submitMutation.isPending || !isStepValid(currentStep)}
                    data-testid="btn-submit"
                >
                    {submitMutation.isPending ? (
                        <><ButtonSpinner/>{isEditMode ? i18n.common.saving : i18n.common.creating}</>
                    ) : (
                        <><Icon icon={CheckCircle} className="mr-2"/>{isEditMode ? i18n.common.saveChanges : i18n.restrictionRuleRequests.createRequest}</>
                    )}
                </Button>
            )}
        </>
    )

    const wizardTitle = isEditMode
        ? i18n.restrictionRuleRequests.wizard.editTitle({name: name || i18n.restrictionRuleRequests.wizard.defaultName})
        : isUpdateMode
            ? i18n.restrictionRuleRequests.wizard.updateTitle({name: ruleData?.name ?? '…'})
            : i18n.restrictionRuleRequests.wizardTitle

    return (
        <>
            <PageLayout
                data-testid="page-restriction-rule-request-create-wizard"
                variant="wizard"
                title={String(wizardTitle)}
                icon={CONCEPT_ICON.restrictionRuleRequest}
                backTo={true}
                loading={isUpdateMode && (ruleLoading || ruleProfilesLoading)}
                wizardSteps={steps}
                wizardCurrentStep={currentStep}
                wizardNavigation={wizardNavigation}
            >
                <div ref={stepContainerRef} data-testid="restriction-rule-request-step-container">
                    <WizardStepHeading
                        title={steps[currentStep].title}
                        description={stepDescriptions[currentStep]}
                    />
                    {renderStepContent()}
                </div>
            </PageLayout>

            {/* Abandon confirmation dialog */}
            <Dialog open={abandonDialog.isOpen} onOpenChange={abandonDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle data-testid="abandon-dialog-title">
                            {i18n.restrictionRuleRequests.wizard.abandonTitle}
                        </DialogTitle>
                    </DialogHeader>
                    <Stack gap="md">
                        <MutedText>{i18n.restrictionRuleRequests.wizard.abandonDesc}</MutedText>
                        <InlineRow justify="end" gap="sm">
                            <Button variant="outline" onClick={abandonDialog.close} data-testid="btn-abandon-stay">
                                {i18n.restrictionRuleRequests.wizard.abandonStay}
                            </Button>
                            <Button variant="destructive" onClick={confirmAbandon} data-testid="btn-abandon-confirm">
                                {i18n.restrictionRuleRequests.wizard.abandonLeave}
                            </Button>
                        </InlineRow>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    )
}
